import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";
import { API } from "@/App";
import Marquee from "react-fast-marquee";
import {
  TrendingUp,
  Shield,
  BarChart3,
  Users,
  ArrowRight,
  ChevronRight,
  Zap,
  Lock,
  Clock,
  Award,
  Gift,
  Star,
} from "lucide-react";
import { FaBitcoin, FaEthereum } from "react-icons/fa";
import { SiLitecoin, SiRipple } from "react-icons/si";

const LandingPage = () => {
  const [cryptoPrices, setCryptoPrices] = useState([]);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pricesRes, packagesRes] = await Promise.all([
          axios.get(`${API}/crypto/prices`),
          axios.get(`${API}/packages`),
        ]);
        setCryptoPrices(pricesRes.data?.slice(0, 10) || []);
        setPackages(packagesRes.data || []);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const getCryptoIcon = (symbol) => {
    const icons = {
      btc: <FaBitcoin className="w-5 h-5 text-amber-500" />,
      eth: <FaEthereum className="w-5 h-5 text-blue-500" />,
      ltc: <SiLitecoin className="w-5 h-5 text-slate-500" />,
      xrp: <SiRipple className="w-5 h-5 text-slate-600" />,
    };
    return icons[symbol?.toLowerCase()] || <TrendingUp className="w-5 h-5" />;
  };

  const getTierStyles = (tier) => {
    const styles = {
      silver: {
        card: "card-silver",
        text: "text-slate-600",
        gradient: "from-slate-100 to-slate-200",
        icon: "text-slate-500",
      },
      gold: {
        card: "card-gold",
        text: "text-amber-600",
        gradient: "from-amber-50 to-amber-100",
        icon: "text-amber-500",
      },
      platinum: {
        card: "card-platinum",
        text: "text-white",
        gradient: "from-slate-800 to-slate-900",
        icon: "text-slate-300",
      },
    };
    return styles[tier] || styles.silver;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Price Ticker */}
      <div className="ticker-premium">
        <Marquee gradient={false} speed={40} className="py-3">
          <div className="flex gap-12 px-6">
            {cryptoPrices.map((coin) => (
              <div key={coin.id} className="flex items-center gap-3">
                {getCryptoIcon(coin.symbol)}
                <span className="font-medium text-slate-300 uppercase">
                  {coin.symbol}
                </span>
                <span className="font-mono text-white">
                  ${coin.current_price?.toLocaleString()}
                </span>
                <span
                  className={`font-mono text-sm ${
                    coin.price_change_percentage_24h >= 0
                      ? "text-emerald-400"
                      : "text-rose-400"
                  }`}
                >
                  {coin.price_change_percentage_24h >= 0 ? "+" : ""}
                  {coin.price_change_percentage_24h?.toFixed(2)}%
                </span>
              </div>
            ))}
          </div>
        </Marquee>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1642790106117-e829e14a795f?auto=format&fit=crop&w=2000&q=80" 
            alt="Trading background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900/95 via-navy-800/90 to-navy-900/80" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900/50 to-transparent" />
        </div>
        
        {/* Animated overlay elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative z-10 container mx-auto px-6 py-16 lg:py-24">
          <nav className="flex items-center justify-between mb-16">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-gold">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <span className="font-heading text-2xl font-semibold text-white">
                AndInvestTrading
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/packages">
                <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/10">
                  Packages
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/10">
                  Login
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-gold px-6">
                  Get Started
                </Button>
              </Link>
            </div>
          </nav>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 border border-white/20 rounded-full backdrop-blur-sm">
                <Star className="w-4 h-4 text-amber-400" />
                <span className="text-sm text-white/90">
                  Premium Investment Platform
                </span>
              </div>
              <h1 className="font-heading text-5xl lg:text-7xl font-semibold text-white leading-tight">
                Elevate Your{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-500">Wealth</span>
              </h1>
              <p className="text-lg text-slate-300 max-w-xl leading-relaxed">
                Join an exclusive community of investors who trust AndInvestTrading 
                for premium investment packages with exceptional returns. 
                Your journey to financial excellence begins here.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button
                    size="lg"
                    className="btn-gold px-8 py-6 text-lg group"
                    data-testid="hero-get-started-btn"
                  >
                    Start Investing
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/packages">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
                    data-testid="hero-view-packages-btn"
                  >
                    View Packages
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-10 pt-6">
                <div>
                  <p className="text-4xl font-heading font-semibold text-white">$50M+</p>
                  <p className="text-sm text-slate-400">Assets Managed</p>
                </div>
                <div className="w-px h-14 bg-white/20" />
                <div>
                  <p className="text-4xl font-heading font-semibold text-white">15K+</p>
                  <p className="text-sm text-slate-400">Active Investors</p>
                </div>
                <div className="w-px h-14 bg-white/20" />
                <div>
                  <p className="text-4xl font-heading font-semibold text-white">99.9%</p>
                  <p className="text-sm text-slate-400">Uptime</p>
                </div>
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-8 bg-gradient-to-r from-amber-500/20 to-amber-300/10 blur-3xl rounded-full" />
                <Card className="relative bg-white/10 backdrop-blur-xl border-white/20 p-8 animate-float">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300">Portfolio Value</span>
                      <span className="text-emerald-400 text-sm font-medium">+24.5%</span>
                    </div>
                    <p className="text-5xl font-heading font-semibold text-white">
                      $127,384
                    </p>
                    <div className="h-40 bg-gradient-to-t from-amber-500/20 to-transparent rounded-xl flex items-end justify-center pb-4">
                      <div className="flex gap-1 items-end">
                        {[40, 60, 45, 80, 65, 90, 75, 95, 85, 100].map((h, i) => (
                          <div
                            key={i}
                            className="w-6 bg-gradient-to-t from-amber-500 to-amber-300 rounded-t"
                            style={{ height: `${h}%` }}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      {[
                        { coin: "BTC", change: 8.52 },
                        { coin: "ETH", change: 12.34 },
                        { coin: "SOL", change: 5.67 }
                      ].map((item) => (
                        <div
                          key={item.coin}
                          className="bg-white/5 rounded-xl p-4 text-center border border-white/10"
                        >
                          <p className="text-xs text-slate-400">{item.coin}</p>
                          <p className="text-sm font-mono text-emerald-400">
                            +{item.change}%
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl lg:text-5xl font-semibold text-platinum mb-4">
              Why Choose AndInvestTrading?
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto text-lg">
              Experience world-class investment management with our cutting-edge platform
              designed for discerning investors.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Shield className="w-8 h-8" />,
                title: "Bank-Level Security",
                description:
                  "Your investments are protected with enterprise-grade encryption and multi-layer security protocols.",
              },
              {
                icon: <BarChart3 className="w-8 h-8" />,
                title: "Real-Time Analytics",
                description:
                  "Access live market data and advanced analytics to make informed investment decisions.",
              },
              {
                icon: <Clock className="w-8 h-8" />,
                title: "24/7 Trading",
                description:
                  "Trade anytime, anywhere with our platform that never sleeps. Global markets at your fingertips.",
              },
              {
                icon: <Gift className="w-8 h-8" />,
                title: "$100 Referral Bonus",
                description:
                  "Earn $100 for every friend you refer who makes their first investment. Grow together.",
              },
            ].map((feature, index) => (
              <Card
                key={index}
                className="bg-white border-slate-200 hover:border-amber-300 hover:shadow-luxury-hover transition-all duration-300 group"
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl flex items-center justify-center text-amber-600 mb-6 group-hover:scale-110 transition-transform">
                    {feature.icon}
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-platinum mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-slate-500">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Packages Section */}
      <section className="py-24 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl lg:text-5xl font-semibold text-platinum mb-4">
              Investment Packages
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto text-lg">
              Choose the package that aligns with your investment goals. All packages
              include professional management and 24/7 support.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {packages
              .sort((a, b) => {
                const order = { silver: 1, gold: 2, platinum: 3 };
                return order[a.tier] - order[b.tier];
              })
              .map((pkg, index) => {
                const styles = getTierStyles(pkg.tier);
                const isPopular = pkg.tier === "gold";
                const isPlatinum = pkg.tier === "platinum";
                return (
                  <Card
                    key={pkg.package_id}
                    className={`relative ${styles.card} overflow-hidden package-card group transition-all duration-300 hover:scale-105`}
                    data-testid={`package-card-${pkg.tier}`}
                  >
                    {isPopular && (
                      <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-amber-400 to-amber-500 text-center py-2.5">
                        <span className="text-sm font-semibold text-white flex items-center justify-center gap-2">
                          <Star className="w-4 h-4" /> Most Popular
                        </span>
                      </div>
                    )}
                    <CardContent className={`p-8 ${isPopular ? "pt-14" : ""}`}>
                      <div className="mb-8">
                        <h3
                          className={`font-heading text-3xl font-semibold ${isPlatinum ? "text-white" : styles.text} mb-2`}
                        >
                          {pkg.name}
                        </h3>
                        <p className={`text-sm ${isPlatinum ? "text-slate-400" : "text-slate-500"}`}>
                          {pkg.duration_days} days investment period
                        </p>
                      </div>

                      <div className="mb-8">
                        <div className="flex items-baseline gap-2">
                          <span className={`text-5xl font-heading font-semibold ${isPlatinum ? "text-amber-400" : "text-platinum"}`}>
                            {pkg.roi_percentage}%
                          </span>
                          <span className={isPlatinum ? "text-slate-400" : "text-slate-500"}>ROI</span>
                        </div>
                        <p className={`text-sm mt-2 ${isPlatinum ? "text-slate-400" : "text-slate-500"}`}>
                          ${pkg.min_investment.toLocaleString()} - $
                          {pkg.max_investment.toLocaleString()}
                        </p>
                      </div>

                      <ul className="space-y-4 mb-8">
                        {pkg.features?.map((feature, i) => (
                          <li
                            key={i}
                            className={`flex items-start gap-3 text-sm ${isPlatinum ? "text-slate-300" : "text-slate-600"}`}
                          >
                            <ChevronRight className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isPlatinum ? "text-amber-400" : "text-amber-500"}`} />
                            {feature}
                          </li>
                        ))}
                      </ul>

                      <Link to="/register">
                        <Button
                          className={`w-full py-6 text-base font-medium ${
                            isPopular || isPlatinum
                              ? "btn-gold"
                              : "btn-premium"
                          }`}
                          data-testid={`invest-btn-${pkg.tier}`}
                        >
                          Start Investing
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                );
              })}
          </div>
        </div>
      </section>

      {/* Referral Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="referral-card overflow-hidden">
              <CardContent className="p-12">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6">
                      <Gift className="w-5 h-5" />
                      <span className="font-medium">Referral Program</span>
                    </div>
                    <h3 className="font-heading text-4xl font-semibold mb-4">
                      Earn $100 Per Referral
                    </h3>
                    <p className="text-platinum/80 mb-6">
                      Share your unique referral code with friends. When they make their 
                      first investment, you'll receive a $100 bonus credited to your account.
                    </p>
                    <Link to="/register">
                      <Button className="btn-premium">
                        Start Referring
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                  </div>
                  <div className="text-center">
                    <div className="text-8xl font-heading font-bold text-white/90">$100</div>
                    <p className="text-platinum/70 mt-2">Per successful referral</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1639762681485-074b7f938ba0?auto=format&fit=crop&w=2000&q=80" 
            alt="Bitcoin background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900/95 via-navy-800/90 to-navy-900/95" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-4xl lg:text-5xl font-semibold text-white mb-6">
              Ready to Elevate Your Portfolio?
            </h2>
            <p className="text-lg text-slate-300 mb-10">
              Join an exclusive community of investors who have already discovered
              the power of premium investment management.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/register">
                <Button
                  size="lg"
                  className="btn-gold px-10 py-6 text-lg"
                  data-testid="cta-create-account-btn"
                >
                  Create Free Account
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-navy-900">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <span className="font-heading text-xl font-semibold text-white">
                AndInvestTrading
              </span>
            </div>
            <div className="flex items-center gap-8 text-sm text-slate-400">
              <Link to="/packages" className="hover:text-white transition-colors">
                Packages
              </Link>
              <span className="hover:text-white transition-colors cursor-pointer">Terms of Service</span>
              <span className="hover:text-white transition-colors cursor-pointer">Privacy Policy</span>
            </div>
            <p className="text-sm text-slate-500">
              &copy; 2026 AndInvestTrading. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
